import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface QuickEstimateFormProps {
  estimateData: any;
  onChange: (data: any) => void;
}

const materialOptions = [
  { value: 'asphalt', label: 'Asphalt Shingles', costPerSq: 350 },
  { value: 'metal', label: 'Metal Roofing', costPerSq: 850 },
  { value: 'tile', label: 'Tile', costPerSq: 1200 },
  { value: 'flat', label: 'Flat/TPO', costPerSq: 600 },
];

const complexityOptions = [
  { value: 'simple', label: 'Simple', multiplier: 1.0 },
  { value: 'moderate', label: 'Moderate', multiplier: 1.2 },
  { value: 'complex', label: 'Complex', multiplier: 1.4 },
];

export function QuickEstimateForm({ estimateData, onChange }: QuickEstimateFormProps) {
  const handleChange = (field: string, value: any) => {
    onChange({ ...estimateData, [field]: value });
  };

  const roofSquares = estimateData.roofSquares || 20;
  const selectedMaterial = materialOptions.find(m => m.value === estimateData.material) || materialOptions[0];
  const selectedComplexity = complexityOptions.find(c => c.value === estimateData.complexity) || complexityOptions[0];

  // Calculate total
  const basePrice = roofSquares * selectedMaterial.costPerSq;
  const total = Math.round(basePrice * selectedComplexity.multiplier);

  return (
    <div className="space-y-4">
      {/* Roof Squares Slider */}
      <Card className="p-6">
        <Label className="text-lg font-semibold">Roof Size (Squares)</Label>
        <div className="flex items-baseline gap-2 mb-4 mt-2">
          <span className="text-4xl font-bold text-primary">{roofSquares}</span>
          <span className="text-muted-foreground">squares</span>
        </div>
        <Slider
          value={[roofSquares]}
          onValueChange={([value]) => handleChange('roofSquares', value)}
          min={5}
          max={100}
          step={1}
          className="mb-2"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>5 sq</span>
          <span>100 sq</span>
        </div>
      </Card>

      {/* Material Selection */}
      <Card className="p-6">
        <Label className="text-lg font-semibold mb-3 block">Material Type</Label>
        <div className="grid grid-cols-2 gap-2">
          {materialOptions.map((material) => (
            <Button
              key={material.value}
              variant="outline"
              className={cn(
                "h-auto py-4 px-3 flex flex-col items-start",
                estimateData.material === material.value && "border-primary bg-primary/5"
              )}
              onClick={() => handleChange('material', material.value)}
            >
              <span className="font-semibold text-sm">{material.label}</span>
              <span className="text-xs text-muted-foreground mt-1">
                ${material.costPerSq}/sq
              </span>
            </Button>
          ))}
        </div>
      </Card>

      {/* Complexity */}
      <Card className="p-6">
        <Label className="text-lg font-semibold mb-3 block">Roof Complexity</Label>
        <div className="grid grid-cols-3 gap-2">
          {complexityOptions.map((complexity) => (
            <Button
              key={complexity.value}
              variant="outline"
              className={cn(
                "h-auto py-4 flex flex-col",
                estimateData.complexity === complexity.value && "border-primary bg-primary/5"
              )}
              onClick={() => handleChange('complexity', complexity.value)}
            >
              <span className="font-semibold text-sm">{complexity.label}</span>
              <span className="text-xs text-muted-foreground mt-1">
                {complexity.multiplier}x
              </span>
            </Button>
          ))}
        </div>
      </Card>

      {/* Estimate Total */}
      <Card className="p-6 bg-primary/5 border-primary/20">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Estimated Total</p>
            <p className="text-3xl font-bold text-primary mt-1">
              ${total.toLocaleString()}
            </p>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <div>{roofSquares} squares</div>
            <div>{selectedMaterial.label}</div>
            <div>{selectedComplexity.label}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
